import { useQuery, useReactiveVar } from "@apollo/client";
import moment from "moment";
import React, { useEffect, useMemo, useRef } from "react";
import {
  GetMeetingOverviewMeetingsQueryQuery,
  GetMeetingOverviewMeetingsQueryQueryVariables,
  MeetingsOverviewMeetingFragment,
} from "types/graphql-schema";

import { calendarUpdatedVar, meetingCreatedVar } from "@cache/cache";
import { assertEdgesNonNull } from "@helpers/helpers";

import getMeetingOverviewMeetingsQuery from "../graphql/get-meeting-overview-meetings-query";

type DateRange = { start: string; end: string };

const EMPTY_MEETINGS: MeetingsOverviewMeetingFragment[] = [];

const MeetingOverviewPage = ({
  pageId,
  range,
  direction,
  minimum,
  participants,
  isOneonone,
  onData,
  pollInterval,
}: {
  pageId: string;
  range: DateRange;
  direction: "future" | "past";
  minimum: number;
  participants: number[];
  isOneonone: boolean | undefined;
  onData: (
    pageId: string,
    meetings: MeetingsOverviewMeetingFragment[],
    loading: boolean,
  ) => void;
  pollInterval?: number;
}) => {
  const { data, loading, refetch, startPolling, stopPolling } = useQuery<
    GetMeetingOverviewMeetingsQueryQuery,
    GetMeetingOverviewMeetingsQueryQueryVariables
  >(getMeetingOverviewMeetingsQuery, {
    variables: {
      participants,
      isOneonone,
      orderBy: "start_datetime",
      first: null,
      minimumDateRange: JSON.stringify({
        minimum,
        dateRange: [range.start, range.end],
        direction,
      }),
    },
    fetchPolicy: "cache-and-network",
  });

  // Poll only when the page is visible
  useEffect(() => {
    if (!pollInterval) return;

    const start = () => {
      if (document.visibilityState === "visible") {
        startPolling(pollInterval);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        startPolling(pollInterval);
      } else {
        stopPolling();
      }
    };

    start();
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [pollInterval, startPolling, stopPolling]);

  const meetings = useMemo(
    () => (data?.meetings ? assertEdgesNonNull(data.meetings) : EMPTY_MEETINGS),
    [data?.meetings],
  );

  useEffect(() => {
    onData(pageId, meetings, loading);
  }, [pageId, meetings, loading, onData]);

  // Refetch when a calendar sync completes (via Pusher)
  const calendarUpdated = useReactiveVar(calendarUpdatedVar);
  const lastCalendarUpdateRef = useRef(calendarUpdated);

  useEffect(() => {
    if (calendarUpdated !== lastCalendarUpdateRef.current) {
      lastCalendarUpdateRef.current = calendarUpdated;
      refetch();
    }
  }, [calendarUpdated, refetch]);

  // Watch for new meetings that fall in this page's range
  const meetingCreated = useReactiveVar(meetingCreatedVar);
  const lastProcessedRef = useRef<typeof meetingCreated>(null);

  useEffect(() => {
    if (!meetingCreated || meetingCreated === lastProcessedRef.current) return;
    lastProcessedRef.current = meetingCreated;

    const meetingTime = moment(meetingCreated.startDatetime);
    if (meetingTime.isBetween(range.start, range.end, undefined, "[]")) {
      refetch();
    }
  }, [meetingCreated, range.start, range.end, refetch]);

  return null;
};

export default React.memo(MeetingOverviewPage);
